// src/features/organisation/components/employees/EmployeeQuickForm.tsx
import React, { useState, useEffect } from 'react';
import { useEmployeeActions } from '../../hooks/useEmployees';
import { Employee, EmployeeCreateDto } from '../../types/employees.types';
import { useSites } from '../../hooks/useSites';
import { useDepartments } from '../../hooks/useDepartments';
import { X } from 'lucide-react';

interface EmployeeQuickFormProps {
  employee?: Employee | null;
  onClose: () => void;
}

const EmployeeQuickForm: React.FC<EmployeeQuickFormProps> = ({ employee, onClose }) => {
  const { createEmployee, updateEmployee, loading } = useEmployeeActions();
  const { sites } = useSites();
  const { departments } = useDepartments();

  const [formData, setFormData] = useState<EmployeeCreateDto>({
    employeeCode: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    siteId: 0,
    departmentId: undefined,
    positionId: undefined,
    managerId: undefined,
    hireDate: '',
    birthDate: '',
    statusCode: 'ACTIVE',
    isQhseResponsible: false,
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (employee) {
      setFormData({
        employeeCode: employee.employeeCode,
        firstName: employee.firstName,
        lastName: employee.lastName,
        email: employee.email,
        phone: employee.phone || '',
        mobile: employee.mobile || '',
        siteId: employee.site.id,
        departmentId: employee.department?.id,
        positionId: employee.position?.id,
        managerId: employee.manager?.id,
        hireDate: employee.hireDate || '',
        birthDate: employee.birthDate || '',
        statusCode: employee.employeeStatus.code,
        isQhseResponsible: employee.isQhseResponsible,
        notes: employee.notes || '',
      });
    }
  }, [employee]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.employeeCode.trim()) {
      newErrors.employeeCode = 'Le code employé est obligatoire';
    }
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est obligatoire';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est obligatoire';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    if (!formData.siteId) {
      newErrors.siteId = 'Le site est obligatoire';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (employee) {
        await updateEmployee(employee.id, formData);
      } else {
        await createEmployee(formData);
      }
      onClose();
    } catch (error) {
      setErrors({ general: 'Erreur lors de la sauvegarde. Veuillez réessayer.' });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name.includes('Id') ? parseInt(value) || undefined : value),
    }));

    // Effacer l'erreur du champ modifié
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const getFieldError = (fieldName: string) => {
    return errors[fieldName] ? (
      <span className="form-error">{errors[fieldName]}</span>
    ) : null;
  };

  // Vérifier si les données sont chargées
  const hasSites = sites && Array.isArray(sites);
  const hasDepartments = departments && Array.isArray(departments);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {employee ? 'Modifier l\'employé' : 'Nouvel employé'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <span className="text-red-600 text-sm">{errors.general}</span>
            </div>
          )}

          {/* Informations de base */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">
                Code employé *
              </label>
              <input
                type="text"
                name="employeeCode"
                value={formData.employeeCode}
                onChange={handleChange}
                className={`form-input ${errors.employeeCode ? 'border-red-300' : ''}`}
                required
                maxLength={20}
              />
              {getFieldError('employeeCode')}
            </div>

            <div>
              <label className="form-label">
                Site *
              </label>
              <select
                name="siteId"
                value={formData.siteId}
                onChange={handleChange}
                className={`form-input ${errors.siteId ? 'border-red-300' : ''}`}
                required
              >
                <option value="">Sélectionner un site</option>
                {hasSites && sites.map(site => (
                  <option key={site.id} value={site.id}>
                    {site.siteName}
                  </option>
                ))}
              </select>
              {getFieldError('siteId')}
            </div>
          </div>

          {/* Nom et prénom */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">
                Prénom *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`form-input ${errors.firstName ? 'border-red-300' : ''}`}
                required
                maxLength={100}
              />
              {getFieldError('firstName')}
            </div>

            <div>
              <label className="form-label">
                Nom de famille *
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`form-input ${errors.lastName ? 'border-red-300' : ''}`}
                required
                maxLength={100}
              />
              {getFieldError('lastName')}
            </div>
          </div>

          {/* Contact */}
          <div>
            <label className="form-label">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`form-input ${errors.email ? 'border-red-300' : ''}`}
              required
              maxLength={255}
            />
            {getFieldError('email')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">
                Téléphone fixe
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                maxLength={20}
              />
            </div>

            <div>
              <label className="form-label">
                Téléphone mobile
              </label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                className="form-input"
                maxLength={20}
              />
            </div>
          </div>

          {/* Organisation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">
                Département
              </label>
              <select
                name="departmentId"
                value={formData.departmentId || ''}
                onChange={handleChange}
                className="form-input"
              >
                <option value="">Sélectionner un département</option>
                {hasDepartments && departments.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.departmentName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">
                Poste
              </label>
              <input
                type="text"
                placeholder="Poste (optionnel)"
                className="form-input"
                disabled
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">
                Date d'embauche
              </label>
              <input
                type="date"
                name="hireDate"
                value={formData.hireDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div>
              <label className="form-label">
                Date de naissance
              </label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          {/* Options */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="isQhseResponsible"
              checked={formData.isQhseResponsible}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 text-sm text-gray-700">
              Responsable QHSE
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="form-label">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="form-input"
              maxLength={1000}
              placeholder="Notes additionnelles..."
            />
          </div>

          {/* Boutons */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary"
            >
              {loading ? 'Sauvegarde...' : (employee ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeQuickForm;